import { createRoute, Link } from '@tanstack/react-router';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Mono,
  PageHeader,
  ResourceTable,
  SectionCard,
  StatusBadge,
  type Column,
} from '@verydia/ui';
import type { AuditEventResource } from '@verydia/contracts';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { rootRoute } from './root.tsx';
import { describeFailure, useMe } from '../lib/api.ts';
import { useAuth } from '../lib/auth/index.ts';
import { useConsoleContext } from '../lib/console-context.tsx';
import { useOrganizationAudit, usePlatformAudit } from '../lib/identity-queries.ts';

/** `MEMBER_ROLE_CHANGED` is a wire value; "Member role changed" is a sentence. */
function humaniseAction(action: string): string {
  const words = action.toLowerCase().replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const ACTOR_TONE = { USER: 'info', API_CREDENTIAL: 'neutral', SYSTEM: 'neutral' } as const;

const COLUMNS: ReadonlyArray<Column<AuditEventResource>> = [
  {
    key: 'when',
    header: 'When',
    width: 'w-52',
    render: (row) => (
      <span className="text-[var(--vc-text-muted)] tabular-nums">
        {formatTimestamp(row.occurred_at)}
      </span>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    render: (row) => (
      <div className="flex flex-col">
        <span className="font-medium text-[var(--vc-text)]">{humaniseAction(row.action)}</span>
        <Mono className="text-[11.5px] text-[var(--vc-text-subtle)]">{row.action}</Mono>
      </div>
    ),
  },
  {
    key: 'actor',
    header: 'Actor',
    width: 'w-36',
    render: (row) => <StatusBadge tone={ACTOR_TONE[row.actor_type]}>{row.actor_type}</StatusBadge>,
  },
  {
    key: 'resource',
    header: 'Resource',
    width: 'w-64',
    render: (row) => (
      <div className="flex flex-col">
        <span className="text-[var(--vc-text-muted)]">{row.resource_type}</span>
        {row.resource_id ? (
          <Mono className="text-[11.5px] text-[var(--vc-text-subtle)]">{row.resource_id}</Mono>
        ) : null}
      </div>
    ),
  },
  {
    key: 'request',
    header: 'Request',
    width: 'w-56',
    render: (row) =>
      row.request_id ? (
        // The join between what an operator sees here and what the API logged.
        // It is the first thing support asks for, so it is a column rather than
        // something to be found by expanding a row.
        <Mono className="text-[11.5px] text-[var(--vc-text-subtle)]">{row.request_id}</Mono>
      ) : (
        <span className="text-[var(--vc-text-subtle)]">—</span>
      ),
  },
];

function AuditPage() {
  const auth = useAuth();
  const context = useConsoleContext();
  const me = useMe(auth.status === 'authenticated');

  const isOperator = (me.data?.data.platform_roles.length ?? 0) > 0;
  const organizationId = context.organization?.id ?? null;

  // A platform operator can see both the platform-wide log and this
  // organization's. They are genuinely different records — platform events have
  // no organization — so this is a real choice rather than a filter.
  const [scope, setScope] = useState<'organization' | 'platform'>('organization');
  const effectiveScope = isOperator ? scope : 'organization';

  const organizationAudit = useOrganizationAudit(
    effectiveScope === 'organization' ? organizationId : null,
  );
  const platformAudit = usePlatformAudit(effectiveScope === 'platform');
  const query = effectiveScope === 'platform' ? platformAudit : organizationAudit;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Audit Log"
        description="An immutable record of every action taken, with the actor, the resource and the originating request id."
        breadcrumbs={[
          { label: context.organization?.name ?? 'Organization', href: '/' },
          { label: 'Audit Log' },
        ]}
        linkComponent={Link}
      />

      {isOperator ? (
        <div className="flex gap-1" role="tablist" aria-label="Audit scope">
          {(['organization', 'platform'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={scope === option}
              onClick={() => setScope(option)}
              className={
                'rounded-[var(--vc-radius-md)] px-3 py-1.5 text-[12.5px] transition-colors ' +
                'duration-[var(--vc-duration-fast)] focus-visible:outline-2 ' +
                'focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)] ' +
                (scope === option
                  ? 'bg-[var(--vc-surface-hover)] font-medium text-[var(--vc-text)]'
                  : 'text-[var(--vc-text-muted)] hover:bg-[var(--vc-surface-hover)]')
              }
            >
              {option === 'organization'
                ? (context.organization?.name ?? 'This organization')
                : 'Platform'}
            </button>
          ))}
        </div>
      ) : null}

      <SectionCard
        flush
        description="Audit records are append-only. They can be read and exported, but never edited or deleted — including by an owner."
      >
        {query.isPending ? (
          <LoadingState label="Loading audit events" />
        ) : query.isError ? (
          <ErrorState
            description={describeFailure(query.error).message}
            requestId={describeFailure(query.error).requestId}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <ResourceTable
            caption={
              effectiveScope === 'platform' ? 'Platform audit events' : 'Organization audit events'
            }
            columns={COLUMNS}
            rows={query.data.data.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                icon={<ScrollText />}
                title="No audit events yet"
                description="Actions taken in the console and through the API are recorded here as they happen."
              />
            }
          />
        )}
      </SectionCard>
    </div>
  );
}

export const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage,
});
